import { Newspaper } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { ANALYSIS_NAMES, domainName, type IntelligenceNews } from '../api/intelligence';
import { fmtKst, fmtNumber, fmtPct } from '../lib/format';
import { usePollingResource } from '../hooks/usePollingResource';
import ResourceNotice from './ResourceNotice';
import { StatusChip } from './ui/Primitives';

export default function NewsAnalysisCard({ item, sourceBody = false }: {
  item: IntelligenceNews; sourceBody?: boolean;
}) {
  const current = item.analysis_state === 'current';
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  return <article className="metric-tile news-analysis-card" aria-label={`뉴스 분석: ${item.title}`}>
    <div className="news-receipt"><span className="news-source-label">{item.source ?? '출처 미관측'}</span><time dateTime={item.published_at ?? undefined}>발행 {fmtKst(item.published_at)} KST</time></div>
    <div className="card-title-row">
      <StatusChip tone={current ? 'ok' : item.analysis_state === 'failed' ? 'danger' : 'warn'}>
        {ANALYSIS_NAMES[item.analysis_state] ?? '분석 미확인'}
      </StatusChip>
      {current && <StatusChip tone={['CRITICAL', 'HIGH'].includes(item.llm_threat_level ?? '') ? 'danger' : 'info'}>
        위험 {item.llm_threat_level ?? '—'}
      </StatusChip>}
      {item.is_top_story && <span className="badge badge-blue">주요 뉴스</span>}
    </div>
    <h4><Newspaper size={15} aria-hidden="true"/><Link to={`/management/data/news?event_id=${encodeURIComponent(item.event_id)}`}>{item.title}</Link></h4>
    <p className="muted-note">수집 {fmtKst(item.collected_at)} · 분석 {fmtKst(item.llm_evaluated_at)} KST</p>
    <div className="monitor-checks">{item.matched_domains?.map(d => <Link className="badge badge-blue" key={d}
      to={`/management/intelligence?domain=${encodeURIComponent(d)}`}>{domainName(d)}</Link>)}
      {item.matched_keywords?.map(k => <span className="badge" key={k}>{k}</span>)}</div>
    {item.summary_snippet && <p className="news-source-summary"><strong>원문 요약</strong> {item.summary_snippet}</p>}
    <div className={`analysis-reasoning ${current ? '' : 'analysis-unverified'}`}>
      <h5>{current ? 'LLM 해석과 근거' : '저장된 이전·미확인 결과'}</h5>
      {!current && <p className="research-alert">현행 분석으로 집계하지 않습니다. 대기·실패·구버전을 중립 의견으로 해석하지 않습니다.</p>}
      <p>{item.llm_reasoning_summary_ko || '저장된 분석문 없음'}</p>
      {item.llm_key_drivers?.length ? <ul>{item.llm_key_drivers.map((driver, i) => <li key={i}>{driver}</li>)}</ul> : null}
      <dl className="monitor-facts">
        <div><dt>영향 점수 · -1~1</dt><dd>{fmtNumber(item.llm_impact_score, { digits: 3, sign: true })}</dd></div>
        <div><dt>모델 자기 보고 신뢰도</dt><dd>{item.llm_confidence == null ? '—' : fmtPct(item.llm_confidence * 100)}</dd></div>
        <div><dt>크립토 변동성 영향</dt><dd>{item.llm_crypto_volatility_impact ?? '—'}</dd></div>
        <div><dt>저장된 권고 · 자문</dt><dd>{item.llm_recommended_action ?? '—'}</dd></div>
      </dl>
    </div>
    <p className="muted-note">입력 r{item.current_input_revision ?? '?'} · 채점 r{item.scored_input_revision ?? '?'} ·
      분석기 {item.scored_scorer_version ?? '미기록'}<br />신뢰도는 검증된 적중률이 아닙니다. C1 진입은 가격 EMA 규칙을 따릅니다.</p>
    {sourceBody && <>
      <details className="news-evidence" onToggle={e => setEvidenceOpen(e.currentTarget.open)}><summary>원문 전체 · 입력과 처리 이력</summary>
        <p className="saved-analysis-text">{item.full_content || '저장된 본문 없음'}</p>
        <p className="muted-note">원문 태그 {item.original_tags?.join(' · ') || '—'}<br />
          원문 종목 표기 {JSON.stringify(item.raw_tickers ?? null)} · 문맥상 연관 표기이며 현재 포지션을 뜻하지 않습니다.</p>
        {evidenceOpen && <NewsLineage eventId={item.event_id}/>}
      </details>
    </>}
  </article>;
}

function NewsLineage({ eventId }: { eventId: string }) {
  const resource = usePollingResource<{
    revisions: { revision: number; title: string; first_seen_at: string; observed_at: string }[];
    jobs: { input_revision: number; state: string; attempts: number; last_error: string | null }[];
    outbox: { input_revision: number; state: string; attempts: number }[];
  }>(`/news/${encodeURIComponent(eventId)}/revisions`, 60000);
  return <div><ResourceNotice {...resource}/>
    <h5>입력 수정과 분석 작업</h5>
    <ul className="monitor-feed">{resource.data?.revisions.map(r => <li key={r.revision}>
      <strong>r{r.revision} · {r.title}</strong><p className="muted-note">처음 관측 {fmtKst(r.first_seen_at ?? r.observed_at)} KST</p>
      {resource.data?.jobs.filter(j => j.input_revision === r.revision).map((j, i) => <p key={i}>
        분석 {j.state} · 시도 {j.attempts} · {j.last_error ?? '저장 오류 없음'}</p>)}
      {resource.data?.outbox.filter(o => o.input_revision === r.revision).map((o, i) => <p key={i}>발행 {o.state} · 시도 {o.attempts}</p>)}
    </li>)}</ul>
    {resource.data?.revisions.length === 0 && <p>리비전 이력 없음 · 레거시 또는 미기록</p>}
  </div>;
}
